/**
 * State of a picture field: the chosen file, its base64 preview, whether the
 * user touched it (so a removal reaches the API too) and whether the server
 * picture is hidden. With `expected` dimensions, a chosen file is measured
 * before being accepted and the validation message is kept for the field.
 */
import { useState, type ChangeEvent, type MouseEvent } from 'react';
import { toast } from 'sonner';
import { readAsDataUrl } from '@/shared/lib/read-file';
import { readImageSize, type ImageSize } from '@/shared/lib/image-size';

export const PICTURE_MAX_KB = 500;

export interface PictureState {
  file: File | null;
  base64: string | null;
  /** The user chose or removed a picture: the change must reach the API. */
  touched: boolean;
  serverHidden: boolean;
  /** Validation message for the last chosen file, including non-blocking warnings. */
  error: string | null;
  /** A chosen file is being measured. */
  validating: boolean;
}

export interface PictureOptions {
  maxKb?: number | null;
  /** Exact dimensions a chosen file must have; null accepts any. */
  expected?: ImageSize | null;
  /** A server picture failing to load counts as touched (the dms-picture-upload component). */
  touchOnServerError?: boolean;
  /** A dimension mismatch is shown inline but does not block the field. */
  dimensionWarningOnly?: boolean;
  /** Overrides the default "Picture should be WxHpx" dimension mismatch message. */
  dimensionErrorMessage?: string;
}

const INITIAL: PictureState = {
  file: null,
  base64: null,
  touched: false,
  serverHidden: false,
  error: null,
  validating: false,
};

const dimensionError = (
  size: ImageSize,
  expected: ImageSize,
  message?: string,
): string | null =>
  size.width === expected.width && size.height === expected.height
    ? null
    : (message ?? `Picture should be ${expected.width}x${expected.height}px`);

export const usePicture = (options: PictureOptions | number | null = {}) => {
  const {
    maxKb = PICTURE_MAX_KB,
    expected = null,
    touchOnServerError = false,
    dimensionWarningOnly = false,
    dimensionErrorMessage,
  } = typeof options === 'object' && options !== null
    ? options
    : { maxKb: options };
  const [state, setState] = useState<PictureState>(INITIAL);

  const fail = (message: string, suppressToast = false) => {
    // Some errors are intentionally not shown as toasts.
    if (!suppressToast) toast.error(message);
    setState((prev) => ({ ...prev, error: message, validating: false }));
  };

  /** Resolves with the dimension error of the file, null when it fits. */
  const checkDimensions = async (
    file: File,
    dimensions: ImageSize | null,
  ): Promise<string | null> => {
    if (!dimensions) return null;
    try {
      return dimensionError(
        await readImageSize(file),
        dimensions,
        dimensionErrorMessage,
      );
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    }
  };

  /**
   * The expected dimensions changed (the element type);
   * the chosen file is measured again, or its error cleared when any size fits.
   */
  const revalidate = (dimensions: ImageSize | null) => {
    const file = state.file;
    if (!file || !dimensions) {
      setState((prev) => ({ ...prev, error: null, validating: false }));
      return;
    }
    setState((prev) => ({ ...prev, validating: true }));
    void checkDimensions(file, dimensions).then((message) => {
      if (!message) {
        setState((prev) => ({ ...prev, error: null, validating: false }));
      } else if (dimensionWarningOnly) {
        setState((prev) => ({ ...prev, error: message, validating: false }));
      } else {
        fail(message, message === dimensionErrorMessage);
      }
    });
  };

  const onChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (maxKb !== null && file.size / 1024 > maxKb) {
      fail(`File should be smaller than ${maxKb}kb`);
      return;
    }
    setState((prev) => ({ ...prev, validating: true }));
    const message = await checkDimensions(file, expected);
    if (message) {
      if (!dimensionWarningOnly) {
        fail(message, message === dimensionErrorMessage);
        return;
      }
    }
    setState({
      file,
      base64: null,
      touched: true,
      serverHidden: true,
      error: dimensionWarningOnly ? message : null,
      validating: false,
    });
    const base64 = await readAsDataUrl(file);
    setState((prev) => (prev.file === file ? { ...prev, base64 } : prev));
  };

  const onRemoved = (event: MouseEvent) => {
    event.preventDefault();
    setState((prev) =>
      prev.file === null && prev.base64 === null && prev.serverHidden
        ? prev
        : {
            file: null,
            base64: null,
            touched: true,
            serverHidden: true,
            error: null,
            validating: false,
          },
    );
  };

  const hideServerPicture = () =>
    setState((prev) => ({
      ...prev,
      serverHidden: true,
      touched: prev.touched || touchOnServerError,
    }));

  return {
    ...state,
    expected,
    onChosen,
    onRemoved,
    hideServerPicture,
    revalidate,
  };
};

export type PictureController = ReturnType<typeof usePicture>;

/**
 * A chosen file, or the stored picture when the field
 * was not touched (a removal counts as touched).
 */
export const hasPicture = (
  picture: Pick<PictureController, 'file' | 'touched'>,
  storedPicture: string | null | undefined,
): boolean => !!picture.file || (!picture.touched && !!storedPicture);

/**
 * The message under a picture field: the last validation message, else a
 * missing required picture once submitted.
 */
export const pictureMessage = (
  picture: Pick<PictureController, 'file' | 'error' | 'serverHidden'>,
  serverUrl: string | null,
  required: boolean,
  submitted: boolean,
): string | null => {
  if (picture.error) return picture.error;
  const hasServer = !!serverUrl && !picture.serverHidden;
  if (submitted && required && !picture.file && !hasServer)
    return 'Picture is required';
  return null;
};
